import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

const GA_MEASUREMENT_ID = 'G-2ZBZL0NMD6';
const GOOGLE_ADS_ID = 'AW-18427461710';
const STORAGE_KEY = 'sami_cookie_consent_v1';

type GoogleConsentState = 'granted' | 'denied';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private initialized = false;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  get isAccepted(): boolean {
    return isPlatformBrowser(this.platformId) && localStorage.getItem(STORAGE_KEY) === 'accepted';
  }

  initFromSavedConsent(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.load(false);
    this.updateConsent(this.isAccepted ? 'granted' : 'denied');

    if (this.isAccepted) this.trackCurrentPageView();
  }

  accept(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    localStorage.setItem(STORAGE_KEY, 'accepted');
    this.load(false);
    this.updateConsent('granted');
  }

  reject(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    localStorage.setItem(STORAGE_KEY, 'rejected');
    this.load(false);
    this.updateConsent('denied');
  }

  trackPageView(url: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.load(false);
    window.gtag?.('event', 'page_view', {
      page_location: window.location.href,
      page_path: url,
      page_title: document.title,
      send_to: [GA_MEASUREMENT_ID, GOOGLE_ADS_ID],
    });
  }

  trackLead(sede: 'pescara' | 'teramo'): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.load(false);
    window.gtag?.('event', 'generate_lead', {
      form_name: 'preventivo',
      sede,
      send_to: [GA_MEASUREMENT_ID, GOOGLE_ADS_ID],
    });
  }

  trackContact(method: 'phone' | 'whatsapp'): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.load(false);
    window.gtag?.('event', 'contact_click', {
      contact_method: method,
      send_to: [GA_MEASUREMENT_ID, GOOGLE_ADS_ID],
    });
  }

  private trackCurrentPageView(): void {
    this.trackPageView(window.location.pathname + window.location.search);
  }

  private updateConsent(state: GoogleConsentState): void {
    window.gtag?.('consent', 'update', {
      ad_storage: state,
      analytics_storage: state,
      ad_user_data: state,
      ad_personalization: state,
    });
  }

  private load(sendPageView = false): void {
    if (!isPlatformBrowser(this.platformId)) return;

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function (...args: unknown[]) {
      window.dataLayer.push(args);
    };

    const existingTag = document.querySelector<HTMLScriptElement>(
      'script[data-sami-google-tag], script[src*="googletagmanager.com/gtag/js"]'
    );

    if (!this.initialized && !existingTag) {
      // Consent Mode v2 defaults to denied until the visitor explicitly accepts.
      window.gtag('consent', 'default', {
        ad_storage: 'denied',
        analytics_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        wait_for_update: 500,
      });

      window.gtag('js', new Date());
      window.gtag('config', GOOGLE_ADS_ID, { send_page_view: false });
      window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: sendPageView });
    }
    this.initialized = true;

    if (!existingTag) {
      const script = document.createElement('script');
      script.async = true;
      script.dataset['samiGoogleTag'] = '';
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(script);
    }
  }
}
